import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  clearConsentRetry,
  clearStoredStep,
  clearTransientRetry,
  getNextConsentStepUrl,
  markConsentRetry,
  markTransientRetry,
  readConsentApps,
  readConsentRetry,
  readTransientRetryAttempt,
  resolveConsentStep,
  storeConsentApps,
} from '../auth/adminConsent';
import { ConsentApp, fetchConsentApps } from '../api/consentApps';
import { ValidationError, validateSignUpConsent } from '../api/signUp';
import { ConsentRetryCountdown } from '../components/ConsentRetryCountdown';
import { ConsentStepLanding } from '../components/ConsentStepLanding';
import { PageLayout } from '../components/PageLayout';
import { Splash } from '../components/Splash';

// Azure AD raises AADSTS650052 when a referenced service has no service
// principal in the tenant yet. For our consent chain this is always OneNote:
// the message comes in two wordings ("The app needs access to a service
// (\"https://*.onenote.com/\")" and "...trying to access a service '<id>'
// (OneNote) that your organization ... lacks a service principal for"), so we
// match on the error code plus a OneNote mention rather than a fixed prefix.
const ONENOTE_MISSING_CODE = 'AADSTS650052';

const isOneNoteMissing = (errorDescription: string): boolean =>
  errorDescription.includes(ONENOTE_MISSING_CODE) && /onenote/i.test(errorDescription);

// Azure returns this when a service principal name is reported as already
// present for the tenant. The cause we have observed is an app that was
// removed from the tenant and is then re-consented: Azure still has the old
// service principal name reserved. Testing showed that a simple *immediate*
// re-consent of the same step heals it on Azure's side, so we auto-retry the
// failing step exactly once (see `markConsentRetry`/`readConsentRetry`). Only
// if that retry hits the same error again do we fall back to a manual landing
// asking the admin to check the Azure portal themselves. (Other, unknown
// causes for the same code would also land in that manual fallback.)
const SP_ALREADY_PRESENT_CODE = 'AADSTS650051';

// Azure AD aborts a consent redirect with `server_error: AADSTS90092:
// Non-retryable error has occurred` when consent steps are started in quick
// succession. Despite the wording the failure *is* transient — support confirmed
// that restarting the flow makes the same step succeed. So we wait and re-run
// the failing step automatically instead of stranding the admin on an alert.
const TRANSIENT_ERROR_CODE = 'AADSTS90092';

// Delays before the 1st and 2nd automatic retry. The number of entries defines
// the retry budget: once it's used up the admin gets the manual landing.
const RETRY_DELAYS_MS = [5000, 15_000];

interface RegisterRequestParams {
  adminConsent: string;
  tenant: string;
  state: string;
  error: string;
  errorSubcode: string;
  errorDescription: string;
}

interface NextConsentLanding {
  invitationId: string;
  stepIndex: number;
}

interface TransientRetryLanding extends NextConsentLanding {
  /** Retries already spent on this step; the next one is `attempt + 1`. */
  attempt: number;
  /** How long to wait before re-running the step. */
  delayMs: number;
}

const readRequest = (searchParams: URLSearchParams): RegisterRequestParams => ({
  adminConsent: searchParams.get('admin_consent') ?? '',
  tenant: searchParams.get('tenant') ?? '',
  state: searchParams.get('state') ?? '',
  error: searchParams.get('error') ?? '',
  errorSubcode: searchParams.get('error_subcode') ?? '',
  errorDescription: searchParams.get('error_description') ?? '',
});

const isEmpty = (request: RegisterRequestParams) =>
  !request.adminConsent &&
  !request.tenant &&
  !request.state &&
  !request.error &&
  !request.errorDescription;

const isGuid = (value: string): boolean => /^[\dA-Fa-f-]{36}$/.test(value);

/**
 * The admin pressed "Return to the application without consent" on the
 * Microsoft consent screen. Azure AD bounces back with
 * `error=access_denied&error_subcode=cancel` and no error_description, so
 * this is a deliberate cancellation rather than a real failure.
 */
const isUserCancellation = (request: RegisterRequestParams): boolean =>
  request.error === 'access_denied' && request.errorSubcode === 'cancel';

/**
 * Consent callback. After every Azure AD admin-consent redirect the admin
 * lands here. Instead of automatically redirecting them onwards, we render
 * a landing screen with progress + button for the next pending consent in
 * the chain. After the last consent the backend is asked to validate the
 * result before we hand control over to the initialization view.
 */
export const RegisterCallbackPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const [oneNoteInvitationId, setOneNoteInvitationId] = useState<string | null>(null);
  const [cancelledInvitationId, setCancelledInvitationId] = useState<string | null>(null);
  const [isEmptyRequest, setIsEmptyRequest] = useState(false);
  const [spConflictInvitationId, setSpConflictInvitationId] = useState<string | null>(null);
  const [spRetryLanding, setSpRetryLanding] = useState<NextConsentLanding | null>(null);
  const [transientRetryLanding, setTransientRetryLanding] = useState<TransientRetryLanding | null>(
    null,
  );
  const [transientFailedInvitationId, setTransientFailedInvitationId] = useState<string | null>(
    null,
  );
  const [nextLanding, setNextLanding] = useState<NextConsentLanding | null>(null);
  const [validating, setValidating] = useState(false);
  const [apps, setApps] = useState<ConsentApp[] | null>(null);

  useEffect(() => {
    const request = readRequest(searchParams);

    /**
     * Handles `AADSTS650051`. On the first occurrence we render an explanatory
     * landing with a "try again" button that re-runs the *same* consent step
     * (an immediate re-consent heals the reserved service-principal name on
     * Azure's side). We deliberately do **not** auto-redirect — the admin would
     * otherwise see the Microsoft consent screen a second time with no
     * explanation. If the button retry returns the same error, we stop and show
     * the manual Azure-portal landing instead.
     */
    const handleSpConflict = async (req: RegisterRequestParams) => {
      const invitationId = isGuid(req.state) ? req.state : '';
      const step = resolveConsentStep(req.state);
      const failedIndex = step ? Math.max(step.nextStepIndex - 1, 0) : 0;

      const retry = readConsentRetry();
      const alreadyRetried =
        !!invitationId && retry?.invitationId === invitationId && retry.stepIndex === failedIndex;

      // Second identical failure, or we can't identify the invitation: stop
      // retrying and show the manual Azure-portal instruction.
      if (alreadyRetried || !invitationId) {
        clearConsentRetry();
        clearStoredStep();
        setSpConflictInvitationId(invitationId);
        return;
      }

      // First failure: load the app chain so the retry button can rebuild the
      // consent URL, then show the explanatory landing. Re-fetch best-effort
      // if the cached chain is gone (fresh browser session).
      let chain = readConsentApps(invitationId);
      if (!chain) {
        try {
          chain = await fetchConsentApps(invitationId);
          storeConsentApps(invitationId, chain);
        } catch {
          chain = null;
        }
      }

      if (!chain || !chain[failedIndex]) {
        // Couldn't rebuild the retry URL — fall back to the manual landing.
        clearConsentRetry();
        clearStoredStep();
        setSpConflictInvitationId(invitationId);
        return;
      }

      setApps(chain);
      setSpRetryLanding({ invitationId, stepIndex: failedIndex });
    };

    /**
     * Handles `AADSTS90092`. The step failed for a transient reason, so we show
     * a countdown and then re-run the *same* step — no progress is lost, and the
     * pause is what makes the retry succeed. The attempt counter lives in
     * sessionStorage because each attempt is a full-page redirect. Once the
     * budget in `RETRY_DELAYS_MS` is used up, the admin gets a landing that lets
     * them restart the flow themselves.
     */
    const handleTransientError = async (req: RegisterRequestParams) => {
      const invitationId = isGuid(req.state) ? req.state : '';
      const step = resolveConsentStep(req.state);
      const failedIndex = step ? Math.max(step.nextStepIndex - 1, 0) : 0;
      const attempt = invitationId ? readTransientRetryAttempt(invitationId, failedIndex) : 0;

      // Budget used up, or we can't identify the invitation: stop retrying.
      if (!invitationId || attempt >= RETRY_DELAYS_MS.length) {
        clearTransientRetry();
        clearStoredStep();
        setTransientFailedInvitationId(invitationId);
        return;
      }

      // Load the app chain so the retry can rebuild the consent URL. Re-fetch
      // best-effort if the cached chain is gone (fresh browser session).
      let chain = readConsentApps(invitationId);
      if (!chain) {
        try {
          chain = await fetchConsentApps(invitationId);
          storeConsentApps(invitationId, chain);
        } catch {
          chain = null;
        }
      }

      if (!chain || !chain[failedIndex]) {
        // Couldn't rebuild the retry URL — there is nothing to redirect to.
        clearTransientRetry();
        clearStoredStep();
        setTransientFailedInvitationId(invitationId);
        return;
      }

      setApps(chain);
      setTransientRetryLanding({
        invitationId,
        stepIndex: failedIndex,
        attempt,
        delayMs: RETRY_DELAYS_MS[attempt],
      });
    };

    if (isEmpty(request)) {
      setIsEmptyRequest(true);
      return;
    }

    if (request.error || request.errorDescription) {
      if (isUserCancellation(request)) {
        // Deliberate cancellation — offer a friendly restart instead of a
        // raw "access_denied:" alert. `state` carries the invitation id.
        clearStoredStep();
        setCancelledInvitationId(isGuid(request.state) ? request.state : '');
      } else if (request.error === 'access_denied' && isOneNoteMissing(request.errorDescription)) {
        // OneNote has no service principal in the tenant yet. The admin only
        // has to sign in to OneNote once to provision it. Restart the chain
        // afterwards. `state` carries the invitation id.
        clearStoredStep();
        setOneNoteInvitationId(isGuid(request.state) ? request.state : '');
      } else if (request.errorDescription.includes(SP_ALREADY_PRESENT_CODE)) {
        // A removed app's service-principal name is still reserved in the
        // tenant. Auto-retry the failing step once (heals it on Azure's side);
        // a second identical failure shows a manual Azure-portal instruction.
        void handleSpConflict(request);
      } else if (request.errorDescription.includes(TRANSIENT_ERROR_CODE)) {
        // Transient Azure AD failure — wait, then re-run the same step.
        void handleTransientError(request);
      } else {
        setError(`${request.error || 'Error'}: ${request.errorDescription || ''}`);
      }
      return;
    }

    // A non-error callback means the consent went through — if it was the
    // auto-retry of a previously failing step, it healed, so drop the markers.
    clearConsentRetry();
    clearTransientRetry();

    const step = resolveConsentStep(request.state);
    if (!step) {
      setError(t('signup:consentStep:registerCallbackPage:invalid_state'));
      return;
    }

    const proceed = (chain: ConsentApp[]) => {
      setApps(chain);

      // Show landing for the next pending consent step instead of
      // auto-redirecting. The admin presses the button on that landing to
      // navigate to Azure AD.
      if (step.nextStepIndex < chain.length) {
        setNextLanding({
          invitationId: step.invitationId,
          stepIndex: step.nextStepIndex,
        });
        return;
      }

      // All consents granted — validate on the backend, then continue to the
      // initialization view.
      clearStoredStep();
      setValidating(true);
      validateSignUpConsent({
        adminConsent: request.adminConsent,
        tenant: request.tenant,
        state: step.invitationId,
      })
        .then(() => {
          navigate(
            `/initialization?invitationId=${encodeURIComponent(
              step.invitationId,
            )}&tenantId=${encodeURIComponent(request.tenant)}`,
            { replace: true },
          );
        })
        .catch((error_: ValidationError | unknown) => {
          setValidating(false);
          if (typeof error_ === 'object' && error_ !== null && 'status' in error_) {
            const ve = error_ as ValidationError;
            setError(`${ve.status} ${ve.reason}: ${ve.message}`);
          } else {
            setError(t('signup:consentStep:registerCallbackPage:validation_failed'));
          }
        });
    };

    // The consent-app chain was cached when the flow started. If it's missing
    // (e.g. the callback was reached in a fresh browser session) re-fetch it
    // best-effort so the remaining steps can still be rendered.
    const cached = readConsentApps(step.invitationId);
    if (cached) {
      proceed(cached);
    } else {
      fetchConsentApps(step.invitationId)
        .then((result) => {
          storeConsentApps(step.invitationId, result);
          proceed(result);
        })
        .catch(() => {
          setError(t('signup:consentStep:registerCallbackPage:validation_failed'));
        });
    }
  }, [navigate, searchParams, t]);

  if (isEmptyRequest) {
    return (
      <PageLayout>
        <Typography variant="h5" gutterBottom>
          {t('signup:landing:signUpPage:no_invite_title')}
        </Typography>
        <Typography>{t('signup:landing:signUpPage:no_invite_description')}</Typography>
      </PageLayout>
    );
  }

  if (oneNoteInvitationId !== null) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h5" gutterBottom>
            {t('signup:consentStep:registerCallbackPage:onenote_missing_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:onenote_missing_description')}
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            size="large"
            href="https://www.onenote.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ minWidth: 240 }}
          >
            {t('signup:consentStep:registerCallbackPage:onenote_missing_open')}
          </Button>
          {oneNoteInvitationId && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(`/signup/${oneNoteInvitationId}`, { replace: true })}
              sx={{ minWidth: 240 }}
            >
              {t('signup:consentStep:registerCallbackPage:button_retry')}
            </Button>
          )}
        </Stack>
      </PageLayout>
    );
  }

  if (cancelledInvitationId !== null) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h5" gutterBottom>
            {t('signup:consentStep:registerCallbackPage:cancelled_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:cancelled_description')}
          </Typography>
          {cancelledInvitationId && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(`/signup/${cancelledInvitationId}`, { replace: true })}
              sx={{ mt: 1, minWidth: 240 }}
            >
              {t('signup:consentStep:registerCallbackPage:button_retry')}
            </Button>
          )}
        </Stack>
      </PageLayout>
    );
  }

  if (spConflictInvitationId !== null) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h5" gutterBottom>
            {t('signup:consentStep:registerCallbackPage:sp_conflict_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:sp_conflict_description')}
          </Typography>
          {spConflictInvitationId && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(`/signup/${spConflictInvitationId}`, { replace: true })}
              sx={{ mt: 1, minWidth: 240 }}
            >
              {t('signup:consentStep:registerCallbackPage:button_retry')}
            </Button>
          )}
        </Stack>
      </PageLayout>
    );
  }

  if (spRetryLanding) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h5" gutterBottom>
            {t('signup:consentStep:registerCallbackPage:sp_retry_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:sp_retry_description')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              if (!apps) return;
              const url = getNextConsentStepUrl(
                apps,
                spRetryLanding.invitationId,
                spRetryLanding.stepIndex,
              );
              if (url) {
                markConsentRetry({
                  invitationId: spRetryLanding.invitationId,
                  stepIndex: spRetryLanding.stepIndex,
                });
                window.location.assign(url);
              }
            }}
            sx={{ mt: 1, minWidth: 240 }}
          >
            {t('signup:consentStep:registerCallbackPage:button_retry')}
          </Button>
        </Stack>
      </PageLayout>
    );
  }

  if (transientRetryLanding) {
    return (
      <PageLayout>
        <ConsentRetryCountdown
          delayMs={transientRetryLanding.delayMs}
          onElapsed={() => {
            if (!apps) return;
            const url = getNextConsentStepUrl(
              apps,
              transientRetryLanding.invitationId,
              transientRetryLanding.stepIndex,
            );
            if (!url) return;
            markTransientRetry({
              invitationId: transientRetryLanding.invitationId,
              stepIndex: transientRetryLanding.stepIndex,
              attempt: transientRetryLanding.attempt + 1,
            });
            window.location.assign(url);
          }}
        />
      </PageLayout>
    );
  }

  if (transientFailedInvitationId !== null) {
    return (
      <PageLayout>
        <Stack spacing={2.5} alignItems="center">
          <Typography variant="h5" gutterBottom>
            {t('signup:consentStep:registerCallbackPage:transient_failed_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:transient_failed_description')}
          </Typography>
          {transientFailedInvitationId && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={() => navigate(`/signup/${transientFailedInvitationId}`, { replace: true })}
              sx={{ mt: 1, minWidth: 240 }}
            >
              {t('signup:consentStep:registerCallbackPage:button_retry')}
            </Button>
          )}
        </Stack>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <Typography variant="h5" gutterBottom>
          {t('signup:consentStep:registerCallbackPage:error_title')}
        </Typography>
        <Alert severity="error">{error}</Alert>
      </PageLayout>
    );
  }

  if (validating) {
    return (
      <PageLayout>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography variant="h6">
            {t('signup:consentStep:registerCallbackPage:validating_title')}
          </Typography>
          <Typography color="text.secondary">
            {t('signup:consentStep:registerCallbackPage:validating_description')}
          </Typography>
        </Stack>
      </PageLayout>
    );
  }

  if (nextLanding) {
    const app = apps?.[nextLanding.stepIndex];
    if (!app) {
      return (
        <PageLayout>
          <Typography variant="h5">
            {t('signup:consentStep:registerCallbackPage:error_title')}
          </Typography>
          <Alert severity="error">
            {t('signup:consentStep:registerCallbackPage:invalid_state')}
          </Alert>
        </PageLayout>
      );
    }
    return (
      <PageLayout>
        <ConsentStepLanding
          stepIndex={nextLanding.stepIndex}
          totalSteps={apps?.length ?? 0}
          appName={app.name}
          appIcon={app.icon}
          onConsent={() => {
            if (!apps) return;
            const url = getNextConsentStepUrl(
              apps,
              nextLanding.invitationId,
              nextLanding.stepIndex,
            );
            if (url) window.location.assign(url);
          }}
        />
      </PageLayout>
    );
  }

  return (
    <Box>
      <Splash />
    </Box>
  );
};
